import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

interface UseUrlSyncedStateOptions<TUrlState> {
	parse: (search: string) => TUrlState;
	build: (next: TUrlState) => string;
	onExternalChange: (next: TUrlState) => void;
}

interface UseUrlSyncedStateResult<TUrlState> {
	initial: TUrlState;
	writeUrl: (next: TUrlState) => void;
}

export function useUrlSyncedState<TUrlState>(
	options: UseUrlSyncedStateOptions<TUrlState>,
): UseUrlSyncedStateResult<TUrlState> {
	const { parse, build, onExternalChange } = options;
	const location = useLocation();
	const history = useHistory();

	const initial = useMemo(() => parse(location.search), []);

	const buildRef = useRef(build);

	buildRef.current = build;

	const parseRef = useRef(parse);

	parseRef.current = parse;

	const onExternalChangeRef = useRef(onExternalChange);

	onExternalChangeRef.current = onExternalChange;

	const locationRef = useRef(location);

	locationRef.current = location;

	const selfWriteSearchRef = useRef<string | null>(null);
	const lastSeenSearchRef = useRef(location.search);

	const writeUrl = useCallback((next: TUrlState) => {
		const { pathname, search } = locationRef.current;
		const target = buildRef.current(next);

		if (target === `${pathname}${search}`) return;

		const queryIndex = target.indexOf('?');

		selfWriteSearchRef.current = queryIndex === -1 ? '' : target.slice(queryIndex);
		history.replace(target);
	}, [history]);

	useEffect(() => {
		if (location.search === lastSeenSearchRef.current
			&& selfWriteSearchRef.current === null) {
			return;
		}

		if (location.search === selfWriteSearchRef.current) {
			selfWriteSearchRef.current = null;
			lastSeenSearchRef.current = location.search;

			return;
		}

		selfWriteSearchRef.current = null;
		lastSeenSearchRef.current = location.search;
		onExternalChangeRef.current(parseRef.current(location.search));
	}, [location.search]);

	return { initial, writeUrl };
}
